import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import logo from "../traderhub24-logo.svg"
import Footer from './components/Footer'
import LanguageSelector from './components/LanguageSelector'

type Module = 'get-started' | 'trades' | 'analytics' | 'strategies' | 'tools' | 'social-trade' | 'community'

function Documentation({ onBack, onNavigate }: { onBack: () => void; onNavigate?: (page: "home" | "changelog" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "documentation" | "contact") => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation('pages');
  const [activeModule, setActiveModule] = useState<Module>('get-started')
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [showMobileModules, setShowMobileModules] = useState(false)

  const modules: { id: Module; name: string }[] = [
    { id: 'get-started', name: t('documentation.modulesList.getStarted') },
    { id: 'trades', name: t('documentation.modulesList.trades') },
    { id: 'analytics', name: t('documentation.modulesList.analytics') },
    { id: 'strategies', name: t('documentation.modulesList.strategies') },
    { id: 'tools', name: t('documentation.modulesList.tools') },
    { id: 'social-trade', name: t('documentation.modulesList.socialTrade') },
    { id: 'community', name: t('documentation.modulesList.community') },
  ]

  const renderContent = () => {
    switch (activeModule) {
      case 'get-started':
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">{t('documentation.getStarted.title')}</h1>
            <div className="prose max-w-none">
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800">{t('documentation.getStarted.welcome.title')}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('documentation.getStarted.welcome.description')}
                </p>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">{t('documentation.getStarted.creatingAccount.title')}</h2>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">{t('documentation.getStarted.creatingAccount.step1.title')}</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>{t('documentation.getStarted.creatingAccount.step1.step1')}</li>
                      <li>{t('documentation.getStarted.creatingAccount.step1.step2')}</li>
                      <li>{t('documentation.getStarted.creatingAccount.step1.step3')}</li>
                      <li>{t('documentation.getStarted.creatingAccount.step1.step4')}</li>
                    </ol>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Understanding the Dashboard</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Once logged in, you'll see the main dashboard with several key sections:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Market Overview:</strong> Real-time market ticker showing major indices and currencies</li>
                    <li><strong>Trade Journal:</strong> Your recent trades displayed in a clean, organized list</li>
                    <li><strong>Navigation Bar:</strong> Quick access to All Trades, Analytics, Tools, and Strategies</li>
                    <li><strong>Market Hours:</strong> Real-time status of major global exchanges</li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Quick Start Guide</h2>
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">1. Add Your First Trade</h3>
                    <p className="text-gray-700">
                      Click the "Add Trade" button to start tracking your trading activity. Fill in the trade details including 
                      symbol, entry price, stop loss, and take profit levels.
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">2. Explore Analytics</h3>
                    <p className="text-gray-700">
                      Navigate to the Analytics page to view comprehensive performance metrics, charts, and insights about your trading.
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">3. Use Trading Tools</h3>
                    <p className="text-gray-700">
                      Access the Tools page for risk/reward calculators, position sizing tools, and other essential trading calculators.
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Account Types</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Free Plan</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                      <li>Trade tracking</li>
                      <li>Advanced analytics</li>
                      <li>Limited trade journal</li>
                      <li>Limited strategies</li>
                    </ul>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Pro Plan</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                      <li>Everything in Free</li>
                      <li>Full trade journal</li>
                      <li>Unlimited strategies</li>
                      <li>Trade publishing</li>
                      <li>Daily price progress</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )

      case 'trades':
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">Trades Module</h1>
            <div className="prose max-w-none">
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800">Overview</h2>
                <p className="text-gray-700 leading-relaxed">
                  The Trades module is the core of TraderHub24, allowing you to record, manage, and analyze all your trading activity. 
                  You can add individual trades, import trades in bulk, and view comprehensive trade history.
                </p>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Adding a Trade</h2>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Step-by-Step Guide</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Click the "Add Trade" button from the main navigation or dashboard</li>
                      <li>Select the market type (Commodity, Crypto, Forex, or Stock)</li>
                      <li>Choose or enter the trading symbol</li>
                      <li>Select trade type (Spot or Future)</li>
                      <li>Choose position direction (Long or Short)</li>
                      <li>Enter entry price, stop loss, and take profit levels</li>
                      <li>Optionally add quantity, contract size, entry date, and notes</li>
                      <li>Select a strategy if applicable</li>
                      <li>Add setup images if you have charts or screenshots</li>
                      <li>Click "Save Trade" to record your trade</li>
                    </ol>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Trade Fields Explained</h2>
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Required Fields</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Market:</strong> The asset class (Commodity, Crypto, Forex, Stock)</li>
                      <li><strong>Symbol:</strong> The trading pair or instrument (e.g., EURUSD, BTCUSD, AAPL)</li>
                      <li><strong>Entry Price:</strong> The price at which you entered the trade</li>
                      <li><strong>Stop Loss:</strong> Your risk management stop loss level</li>
                      <li><strong>Take Profit:</strong> Your profit target level</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Optional Fields</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Quantity:</strong> Number of units/contracts</li>
                      <li><strong>Contract Size:</strong> Size per contract (for futures)</li>
                      <li><strong>Entry Date:</strong> When you entered the trade</li>
                      <li><strong>Exit Date & Exit Price:</strong> When you closed the trade (for closed trades)</li>
                      <li><strong>Trade Setup:</strong> Description of your trading setup</li>
                      <li><strong>Notes:</strong> Additional notes or observations</li>
                      <li><strong>Strategy:</strong> Link to a trading strategy</li>
                      <li><strong>Time Interval:</strong> Chart timeframe used</li>
                      <li><strong>Setup Images:</strong> Screenshots of entry and exit setups</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Symbol Selection and Price Updates</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    When adding a trade, you can either select a symbol from the list or enter a custom symbol.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Selectable Symbols (with automatic price updates)</h3>
                    <p className="text-gray-700 mb-2">
                      The following symbol groups are available in the selector and support automatic price updates:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Major cryptocurrencies</strong> (e.g. BTC, ETH and other popular pairs)</li>
                      <li><strong>Nasdaq</strong> listed stocks</li>
                      <li><strong>NYSE</strong> (New York Stock Exchange) stocks</li>
                      <li><strong>London Stock Exchange</strong> (LSE) stocks</li>
                      <li><strong>Borsa Istanbul</strong> (BIST) stocks</li>
                    </ul>
                    <p className="text-gray-700 mt-2">
                      For symbols chosen from this list, open Stock and Crypto positions are tracked in a central price table. Prices are fetched from Yahoo Finance every 5 minutes while the exchange is open (crypto updates 24/7).
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Custom Symbols (no price updates)</h3>
                    <p className="text-gray-700">
                      If the symbol you need is not in the system, you can type it in manually. Custom symbols are fully supported for recording and analyzing trades, but <strong>automatic price updates are not available</strong> for these entries. You can still edit prices manually whenever you want.
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Viewing All Trades</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    The "All Trades" page provides a comprehensive view of all your trades with advanced filtering and sorting capabilities.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Features</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li><strong>Filtering:</strong> Filter by market, symbol, strategy, date range, trade status, and more</li>
                      <li><strong>Sorting:</strong> Sort by any column (date, P&L, R:R ratio, etc.)</li>
                      <li><strong>Search:</strong> Quickly find trades by symbol or other criteria</li>
                      <li><strong>Export:</strong> Export your trades to Excel for external analysis</li>
                      <li><strong>Edit/Delete:</strong> Update or remove trades as needed</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Importing Trades</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    You can import multiple trades at once by copying data from Excel or Google Sheets. This is especially useful when migrating from other platforms or bulk importing historical trades.
                  </p>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-gray-800 mb-2">How to Import</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Go to the "All Trades" page</li>
                      <li>Click the "Import Trades" button</li>
                      <li>Copy your data from Excel or Google Sheets (including headers)</li>
                      <li>Make sure the first row contains column headers</li>
                      <li>Paste the data in the text area provided</li>
                      <li>Click "Preview" to see how the data will be imported</li>
                      <li>Review the preview and click "Import" to add the trades to your journal</li>
                    </ol>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Required Columns</h3>
                    <p className="text-gray-700 mb-2">The following columns are required for import:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Symbol:</strong> The trading symbol (e.g., EURUSD, BTCUSD, AAPL)</li>
                      <li><strong>Entry Price:</strong> The price at which you entered the trade</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Supported Columns</h3>
                    <p className="text-gray-700 mb-2">You can include any of the following columns in your import:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                      <ul className="list-disc list-inside space-y-1">
                        <li>Symbol</li>
                        <li>Market</li>
                        <li>Direction (Long/Short)</li>
                        <li>Entry Date</li>
                        <li>Entry Price</li>
                        <li>Exit Date</li>
                        <li>Exit Price</li>
                        <li>Stop Loss</li>
                        <li>Take Profit</li>
                        <li>RR</li>
                        <li>Quantity</li>
                      </ul>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Contract Size</li>
                        <li>P&L</li>
                        <li>P&L %</li>
                        <li>Currency</li>
                        <li>Strategy</li>
                        <li>Trade Setup</li>
                        <li>Notes</li>
                        <li>Trade Type</li>
                        <li>Position Size</li>
                        <li>Time Interval</li>
                        <li>Tags (comma or space separated)</li>
                        <li>Feed (true/false for public visibility)</li>
                        <li>Account (name or platform)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Closing a Trade</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    When you close a trade, update it with the exit date and exit price. The system will automatically calculate:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Profit/Loss (P&L) in dollars</li>
                    <li>P&L as a percentage</li>
                    <li>Risk/Reward ratio</li>
                    <li>Trade duration</li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
        )

      case 'analytics':
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">Analytics Module</h1>
            <div className="prose max-w-none">
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800">Overview</h2>
                <p className="text-gray-700 leading-relaxed">
                  The Analytics module provides comprehensive insights into your trading performance. Analyze your trades by time period, 
                  strategy, market type, and more to identify patterns and improve your trading decisions.
                </p>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Key Metrics</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Performance Metrics</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                      <li>Total P&L (Profit & Loss)</li>
                      <li>Win Rate</li>
                      <li>Profit Factor</li>
                      <li>Average Win/Loss</li>
                      <li>Largest Win/Loss</li>
                      <li>Expectancy</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Risk Metrics</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                      <li>Risk/Reward Ratios</li>
                      <li>Maximum Drawdown</li>
                      <li>Sharpe Ratio</li>
                      <li>Risk per Trade</li>
                      <li>Position Sizing Analysis</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Using Time Filters</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Filter your analytics by different time periods to analyze performance over specific intervals:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>All:</strong> View all trades regardless of date</li>
                      <li><strong>1D, 1W, 1M:</strong> Last day, week, or month</li>
                      <li><strong>3M, 6M:</strong> Last 3 or 6 months</li>
                      <li><strong>YTD:</strong> Year to date</li>
                      <li><strong>1Y:</strong> Last year</li>
                      <li><strong>Custom:</strong> Select your own date range</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Strategy Analysis</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Analyze performance by strategy to identify which trading strategies work best for you:
                  </p>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-gray-800 mb-2">How to Use</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Select a strategy from the strategy dropdown filter</li>
                      <li>View metrics specific to that strategy</li>
                      <li>Compare different strategies to find the most profitable ones</li>
                      <li>Use insights to refine or focus on high-performing strategies</li>
                    </ol>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Charts and Visualizations</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    The Analytics page includes various charts to visualize your trading performance:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li><strong>P&L Over Time:</strong> See your cumulative profit/loss progression</li>
                    <li><strong>Monthly Performance:</strong> Compare performance across different months</li>
                    <li><strong>Market Distribution:</strong> See how your trades are distributed across markets</li>
                    <li><strong>Win/Loss Distribution:</strong> Visualize the distribution of winning vs losing trades</li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Interpreting Your Analytics</h2>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Key Indicators to Watch</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li><strong>Win Rate:</strong> Aim for consistency. A 50%+ win rate with good R:R is excellent</li>
                      <li><strong>Profit Factor:</strong> Values above 1.5 indicate profitable trading</li>
                      <li><strong>Average R:R:</strong> Higher risk/reward ratios generally lead to better long-term results</li>
                      <li><strong>Drawdown:</strong> Monitor maximum drawdown to assess risk management</li>
                      <li><strong>Expectancy:</strong> Positive expectancy means profitable over the long term</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )

      case 'strategies':
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">Strategies Module</h1>
            <div className="prose max-w-none">
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800">Overview</h2>
                <p className="text-gray-700 leading-relaxed">
                  The Strategies module allows you to create, manage, and track the performance of your trading strategies. 
                  Organize your trades by strategy and analyze which approaches work best for your trading style.
                </p>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Creating a Strategy</h2>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Step-by-Step Guide</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Navigate to the Strategies page</li>
                      <li>Click the "Create Strategy" button</li>
                      <li>Enter a strategy name (this will be the base name)</li>
                      <li>Add a description explaining your strategy</li>
                      <li>Define entry rules (when to enter trades)</li>
                      <li>Define exit rules (when to exit trades)</li>
                      <li>Optionally upload an image representing your strategy</li>
                      <li>Set strategy as active or inactive</li>
                      <li>Choose if the strategy should be public (visible to others)</li>
                      <li>Click "Create Strategy" to save</li>
                    </ol>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Strategy Fields Explained</h2>
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Required Fields</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Name:</strong> A unique identifier for your strategy (e.g., "Breakout Trading", "Mean Reversion")</li>
                      <li><strong>Entry Rules:</strong> Clear criteria for when to enter a trade</li>
                      <li><strong>Exit Rules:</strong> Clear criteria for when to exit a trade</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Optional Fields</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Description:</strong> Detailed explanation of your strategy</li>
                      <li><strong>Image:</strong> Visual representation (chart, diagram, etc.)</li>
                      <li><strong>Public:</strong> Make strategy visible to other users</li>
                      <li><strong>Active:</strong> Enable/disable the strategy</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Strategy Versions</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    When you update a strategy, TraderHub24 automatically creates a new version while keeping the old version for historical reference. 
                    This allows you to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Track how your strategy evolves over time</li>
                    <li>Compare performance across different versions</li>
                    <li>Maintain historical accuracy of past trades</li>
                    <li>Revert to previous versions if needed</li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Linking Trades to Strategies</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    When adding or editing a trade, you can link it to a strategy. This enables:
                  </p>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>Performance tracking per strategy</li>
                      <li>Strategy-specific analytics</li>
                      <li>Identifying which strategies are most profitable</li>
                      <li>Organizing trades by trading approach</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Strategy Performance</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Each strategy displays key performance metrics:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Total Trades:</strong> Number of trades using this strategy</li>
                      <li><strong>Win Rate:</strong> Percentage of profitable trades</li>
                      <li><strong>Total P&L:</strong> Cumulative profit/loss for this strategy</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Managing Strategies</h2>
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Available Actions</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Edit:</strong> Update strategy details and create a new version</li>
                      <li><strong>View Details:</strong> See full strategy information and all associated trades</li>
                      <li><strong>Delete:</strong> Remove a strategy (be careful - this cannot be undone)</li>
                      <li><strong>Toggle Active:</strong> Enable or disable a strategy without deleting it</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Pro Plan Features</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Pro plan users enjoy unlimited strategies, while free plan users have limited strategy creation. 
                    Upgrade to Pro for unlimited strategy management and advanced features.
                  </p>
                </div>
              </section>
            </div>
          </div>
        )

      case 'tools':
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">Tools Module</h1>
            <div className="prose max-w-none">
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800">Overview</h2>
                <p className="text-gray-700 leading-relaxed">
                  The Tools module provides essential calculators and utilities to help you make informed trading decisions. 
                  These tools assist with risk management, position sizing, and performance analysis.
                </p>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Risk/Reward & Position Size Calculator</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    This calculator helps you determine the optimal position size based on your risk tolerance and trade setup.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">How to Use</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Enter your account size (total capital)</li>
                      <li>Set your risk percentage per trade (typically 1-2%)</li>
                      <li>Enter the entry price for your trade</li>
                      <li>Set your stop loss level</li>
                      <li>Optionally enter target prices (TP1 and TP2)</li>
                      <li>The calculator will automatically show:
                        <ul className="list-disc list-inside ml-4 mt-1">
                          <li>Risk/Reward ratios (RR1, RR2)</li>
                          <li>Suggested position size in units</li>
                          <li>Risk amount in dollars</li>
                          <li>Risk per unit</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-700">
                      <strong>Pro Tip:</strong> Always use proper position sizing. Never risk more than 1-2% of your account on a single trade.
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Win Rate Calculator</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Calculate your win rate to understand your trading performance and consistency.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">How to Use</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Enter the number of winning trades</li>
                      <li>Enter the number of losing trades</li>
                      <li>The calculator automatically displays your win rate percentage</li>
                    </ol>
                    <p className="mt-2 text-sm text-gray-600">
                      Formula: (Winning Trades / Total Trades) × 100
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Profit Factor Calculator</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    The profit factor is a key metric that shows the ratio of gross profit to gross loss. 
                    A profit factor above 1.0 indicates profitable trading.
                  </p>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 className="font-semibold text-gray-800 mb-2">How to Use</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Enter your total profit from all winning trades</li>
                      <li>Enter your total loss from all losing trades</li>
                      <li>The calculator shows your profit factor</li>
                    </ol>
                    <p className="mt-2 text-sm text-gray-600">
                      Formula: Total Profit / Total Loss
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      <strong>Interpretation:</strong>
                    </p>
                    <ul className="list-disc list-inside ml-4 text-sm text-gray-600">
                      <li>&gt; 1.5: Excellent</li>
                      <li>1.0 - 1.5: Good</li>
                      <li>&lt; 1.0: Needs improvement</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Market Hours Tool</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Stay informed about global market hours and trading sessions. The Market Hours tool shows real-time status 
                    of major exchanges around the world.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Features</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>Real-time market status (Open/Closed)</li>
                      <li>Time until market opens or closes</li>
                      <li>Local trading hours for each exchange</li>
                      <li>Support for major exchanges:
                        <ul className="list-disc list-inside ml-4 mt-1">
                          <li>NYSE (New York Stock Exchange)</li>
                          <li>NASDAQ</li>
                          <li>LSE (London Stock Exchange)</li>
                          <li>TSE (Tokyo Stock Exchange)</li>
                          <li>XETR (Frankfurt Stock Exchange)</li>
                          <li>HKEX (Hong Kong Stock Exchange)</li>
                          <li>ASX (Australian Stock Exchange)</li>
                          <li>TSX (Toronto Stock Exchange)</li>
                          <li>BIST (Istanbul Stock Exchange)</li>
                        </ul>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Best Practices</h2>
                <div className="space-y-3">
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Using Trading Tools Effectively</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li>Always calculate position size before entering a trade</li>
                      <li>Use risk/reward ratios to filter trade setups (aim for at least 1:2)</li>
                      <li>Regularly check your win rate and profit factor</li>
                      <li>Monitor market hours to plan your trading sessions</li>
                      <li>Use these tools to backtest and validate your strategies</li>
                      <li>Keep a trading journal to track your calculations and decisions</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )

      case 'social-trade':
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">Social Trade Module</h1>
            <div className="prose max-w-none">
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800">Overview</h2>
                <p className="text-gray-700 leading-relaxed">
                  The Social Trade module allows you to share your trades with others, create trade pools, and manage followers. 
                  This feature is available exclusively for Pro plan users and enables you to build a community around your trading activity.
                </p>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> Social Trade features require a Pro or Publisher subscription.
                  </p>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Making Trades Public</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    To share a trade with others, you need to enable the "Public share" option when adding or editing a trade.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Step-by-Step Guide</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Go to "All Trades" page</li>
                      <li>Click on a trade to edit it, or add a new trade</li>
                      <li>In the trade form, find the "Public share" option</li>
                      <li>Enable the toggle to make the trade public</li>
                      <li>Choose visibility: "Link", "Followers" or "Community"</li>
                      <li>Save the trade</li>
                      <li>The trade will now appear in your Social Trade page</li>
                    </ol>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Visibility Options</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li><strong>Link:</strong> Trade gets a unique shareable link that anyone can access</li>
                      <li><strong>Followers:</strong> Trade is only visible to your followers</li>
                      <li><strong>Community:</strong> Trade appears in the Community feed but doesn't have a direct shareable link</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Managing Public Trades</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    The Social Trade page shows all your trades that have "Public share" enabled. You can:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>View all your public trades in one place</li>
                    <li>Filter by market, direction, or search by symbol</li>
                    <li>Toggle P&L visibility in the shared view</li>
                    <li>Copy share links to share with others</li>
                    <li>Manage individual trade settings</li>
                  </ul>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Trade Pools</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Trade Pools allow you to organize your public trades into groups. This is useful for categorizing trades by strategy, 
                    market, or any other criteria you choose. You can also manage pool-specific followers and control access to trades within each pool.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Creating a Trade Pool</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Go to Social Trade page</li>
                      <li>Click the "Trade Pool" button</li>
                      <li>Enter a name for your new pool in the "New pool name" field</li>
                      <li>Click "Create" to create the pool</li>
                      <li>The pool will appear in your "My pools" list</li>
                    </ol>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Pool Management</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Select a Pool:</strong> Click on any pool from the list to view and manage it</li>
                      <li><strong>Rename:</strong> Click "Rename" to change the pool name</li>
                      <li><strong>Delete:</strong> Click "Delete" to remove a pool (trades will be unassigned from the pool)</li>
                      <li><strong>Assign Trades:</strong> In the pool detail view, assign public trades to this pool using the "Add" button</li>
                      <li><strong>Remove Trades:</strong> Click "Remove" to unassign a trade from the pool</li>
                    </ul>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Pool Options</h3>
                    <p className="text-gray-700 mb-2">Each pool has configurable options:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Add new followers automatically:</strong> When enabled, any follower you add in the Followers page will also be automatically added to this pool</li>
                      <li><strong>Restrict access to pool followers only:</strong> When enabled, trades added to this pool will automatically be set to "restricted" visibility. Only pool followers will be able to access these trades</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 className="font-semibold text-gray-800 mb-2">How Trade Pools Work</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>Trades can belong to <strong>one pool</strong> at a time. Removing a trade from a pool sets it back to "no pool"</li>
                      <li>Followers are stored as emails. They can be <strong>global</strong> (Followers page) or <strong>pool-specific</strong> (added inside a pool)</li>
                      <li>When you add a trade to a pool, pool's followers will automatically get notified about the trade</li>
                      <li>If "Add new followers automatically" is enabled for a pool, every time you add/import a follower in the Followers page, that email is also added to this pool automatically</li>
                      <li>If "Restrict access to pool followers only" is enabled, trades added to this pool will automatically be set to <strong>restricted</strong> visibility. Only followers who are specifically added to this pool will be able to access these trades</li>
                      <li>Pools are private to your account, so each user only sees and manages their own pools, trades, and followers</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Followers Management</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    The Followers feature allows you to manage who can see your trades. You can add followers globally or to specific trade pools, 
                    and control their access to different pools through an intuitive management interface.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Adding Followers</h3>
                    <p className="text-gray-700 mb-2">There are two ways to add followers:</p>
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">Single Follower</h4>
                        <ol className="list-decimal list-inside space-y-1 text-gray-700 text-sm">
                          <li>Go to Social Trade page</li>
                          <li>Click the "Followers" button</li>
                          <li>Enter the email address in the "Add follower" section</li>
                          <li>Click "Add"</li>
                          <li>The follower will be added as a global follower (can see pool'suz trades)</li>
                          <li>If any pools have "Add new followers automatically" enabled, the follower will also be added to those pools</li>
                        </ol>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-1">Bulk Import</h4>
                        <ol className="list-decimal list-inside space-y-1 text-gray-700 text-sm">
                          <li>Go to the Followers page</li>
                          <li>In the "Bulk import" section, paste emails separated by commas, spaces, or new lines</li>
                          <li>The system will parse and validate the emails</li>
                          <li>Click "Import" to add all valid emails</li>
                          <li>Invalid emails will be skipped</li>
                          <li>Duplicate emails will be ignored</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Managing Follower Access</h3>
                    <p className="text-gray-700 mb-2">Each follower's access can be managed through a pop-up modal:</p>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Go to the Followers page</li>
                      <li>Find the follower in the list</li>
                      <li>Click the "Manage" button next to the follower's email</li>
                      <li>A pop-up modal will open showing all access options</li>
                      <li>Use the checkboxes to control access:
                        <ul className="list-disc list-inside ml-4 mt-1">
                          <li><strong>Global Access:</strong> Check/uncheck to allow access to pool'suz trades</li>
                          <li><strong>Pool Access:</strong> Check/uncheck each pool to grant or revoke access to that specific pool</li>
                        </ul>
                      </li>
                      <li>Changes are saved automatically when you toggle checkboxes</li>
                      <li>Click "Close" or click outside the modal to close it</li>
                    </ol>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Follower Access Types</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Global Access:</strong> When enabled, the follower can see all trades that are not assigned to any pool (pool'suz trades). This is useful for giving broad access to your public trades.</li>
                      <li><strong>Pool Access:</strong> When enabled for a specific pool, the follower can see all trades assigned to that pool. A follower can have access to multiple pools simultaneously.</li>
                      <li><strong>Combined Access:</strong> A follower can have both global access and pool access at the same time. They will see:
                        <ul className="list-disc list-inside ml-4 mt-1">
                          <li>All trades (if global access is enabled)</li>
                          <li>All trades in pools they have access to</li>
                        </ul>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Restricted Access Trades</h3>
                    <p className="text-gray-700 mb-2">When a pool has "Restrict access to pool followers only" enabled:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>Trades added to that pool automatically get <strong>restricted</strong> visibility</li>
                      <li>Only followers who have access to that specific pool can view these trades</li>
                      <li>Even if a follower has global access, they cannot see restricted pool trades unless they also have access to that pool</li>
                      <li>This allows you to create exclusive pools for specific groups of followers</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Removing Followers</h3>
                    <p className="text-gray-700 mb-2">You can remove followers in two ways:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Remove All Access:</strong> Click "Remove" next to a follower to remove all their access (global + all pools)</li>
                      <li><strong>Remove Specific Access:</strong> Use the "Manage" modal to uncheck specific access types (global or individual pools)</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Search Functionality</h3>
                    <p className="text-gray-700">
                      Use the search box in the "Your followers" section to quickly find followers by email address. 
                      The search is case-insensitive and matches any part of the email.
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Sharing Trades</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Once a trade is made public with "Link" visibility, it gets a unique shareable link that you can copy and share.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">How to Share</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Go to Social Trade page</li>
                      <li>Find the trade you want to share</li>
                      <li>Click the "Copy Link" button (or similar action)</li>
                      <li>The shareable link is copied to your clipboard</li>
                      <li>Share the link via email, social media, or any other method</li>
                    </ol>
                                     </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Trade Settings</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Each public trade has individual settings that you can customize:
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Available Settings</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Show Quantity:</strong> Toggle whether to display position size/quantity in the shared view</li>
                      <li><strong>Show P&L:</strong> Control whether profit/loss information is visible</li>
                      <li><strong>Visibility:</strong> Switch between Public (with link) and Community (feed only)</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )

      case 'community':
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">Community Module</h1>
            <div className="prose max-w-none">
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800">Overview</h2>
                <p className="text-gray-700 leading-relaxed">
                  The Community module is a social feed where you can discover trades shared by other traders, follow successful traders, 
                  and build a network of trading insights. This feature helps you learn from others and share your own trading journey.
                </p>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Community Feed</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    The Community feed displays trades shared by all traders who have enabled "Community" visibility on their trades. 
                    You can browse this feed even without being logged in.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Features</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>View trades from all community members</li>
                      <li>See trader profile names and trade details</li>
                      <li>Filter by market type (Commodity, Crypto, Forex, Stock)</li>
                      <li>Filter by trade direction (Long/Short)</li>
                      <li>Search trades by symbol</li>
                      <li>Click on any trade to view full details</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Following Traders</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    When logged in, you can follow other traders to see their trades in your personalized "Following" feed. 
                    You can follow traders globally or follow specific trade pools.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-800 mb-2">How to Follow</h3>
                    <ol className="list-decimal list-inside space-y-2 text-gray-700">
                      <li>Browse the Community feed or view a trader's public trade</li>
                      <li>Identify traders whose trades you want to follow</li>
                      <li>Use the Followers management in Social Trade (if you're the trader) or contact the trader to be added as a follower</li>
                      <li>Once added as a follower, their trades will appear in your "Following" tab</li>
                    </ol>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Following Types</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Global Following:</strong> See all public trades from a specific trader</li>
                      <li><strong>Pool Following:</strong> See only trades from a specific trader's trade pool</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Following Feed</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    The "Following" tab shows trades from all traders you follow. This personalized feed helps you focus on 
                    traders whose strategies and performance you want to track.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Following Feed Features</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>See trades only from traders you follow</li>
                      <li>View which traders you're following in the feed header</li>
                      <li>Same filtering options as the Community feed</li>
                      <li>Quick access to trader profiles</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-gray-700">
                      <strong>Note:</strong> The Following feed is only available when you're logged in. 
                      Non-logged-in users will see the Community feed by default.
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Viewing Trade Details</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Clicking on any trade in the Community feed opens a detailed view showing:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Complete trade information (entry, exit, P&L, etc.)</li>
                    <li>Trader profile information</li>
                    <li>Trade setup and notes (if shared)</li>
                    <li>Entry and exit setup images (if provided)</li>
                    <li>Related trades from the same trader</li>
                  </ul>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-700">
                      <strong>Privacy:</strong> Trade details shown depend on what the trader has chosen to share. 
                      Some information may be hidden based on trader settings.
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Filtering and Search</h2>
                <div className="space-y-3">
                  <p className="text-gray-700 leading-relaxed">
                    Use the filter options to find specific trades in the Community feed:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Available Filters</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Market:</strong> Filter by Commodity, Crypto, Forex, or Stock</li>
                      <li><strong>Direction:</strong> Show only Long or Short trades</li>
                      <li><strong>Symbol Search:</strong> Search for specific trading symbols (e.g., EURUSD, BTCUSD, AAPL)</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="space-y-4 mt-8">
                <h2 className="text-2xl font-semibold text-gray-800">Best Practices</h2>
                <div className="space-y-3">
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-gray-800 mb-2">Using Community Effectively</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li>Follow traders whose strategies align with your trading style</li>
                      <li>Use filters to focus on markets you trade</li>
                      <li>Learn from successful traders' trade setups and notes</li>
                      <li>Share your own trades to contribute to the community</li>
                      <li>Respect privacy settings and trader preferences</li>
                      <li>Use the Community feed to discover new trading ideas and strategies</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-app-bg text-gray-900">
      {/* Header */}
      <header className="w-full py-3 px-4 md:px-6 max-w-7xl mx-auto border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img 
              src={logo} 
              alt="TraderHub24" 
              className="h-12 md:h-16 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={onBack} 
            />
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('documentation.title')}</h1>
          </div>
          <button
            onClick={onBack}
            className="w-full md:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-center"
          >
            {t('documentation.backToHome')}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar - Desktop / Dropdown - Mobile */}
          <aside className="w-full md:w-64 flex-shrink-0">
            {/* Mobile Module Selector */}
            <div className="md:hidden mb-4">
              <button
                onClick={() => setShowMobileModules(!showMobileModules)}
                className="w-full flex items-center justify-between px-4 py-3 bg-app-box border border-gray-200 rounded-lg text-gray-700 font-medium"
              >
                <span>{t('documentation.module')}: {modules.find(m => m.id === activeModule)?.name}</span>
                <svg 
                  className={`w-5 h-5 transition-transform ${showMobileModules ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showMobileModules && (
                <div className="mt-2 bg-app-box border border-gray-200 rounded-lg p-2 shadow-lg animate-in fade-in slide-in-from-top-2 duration-200">
                  {modules.map((module) => (
                    <button
                      key={module.id}
                      onClick={() => {
                        setActiveModule(module.id);
                        setShowMobileModules(false);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors mb-1 last:mb-0 ${
                        activeModule === module.id
                          ? 'bg-orange-500 text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {module.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop Sidebar Navigation */}
            <nav className="hidden md:block bg-app-box border border-gray-200 rounded-lg p-4 sticky top-6">
              <div className="mb-4">
                <LanguageSelector alignLeft openDown />
              </div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">{t('documentation.modules')}</h2>
              <ul className="space-y-1">
                {modules.map((module) => (
                  <li key={module.id}>
                    <button
                      onClick={() => {
                        setActiveModule(module.id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                        activeModule === module.id
                          ? 'bg-orange-500 text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {module.name}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Content Area */}
          <main className="flex-1 bg-white border border-gray-200 rounded-lg p-4 md:p-8 min-w-0">
            {renderContent()}
          </main>
        </div>
      </div>

      {/* Footer */}
      <Footer 
        currentPage="documentation" 
        onNavigate={onNavigate || ((page) => {
          if (page === "documentation") return;
          else if (page === "privacy-policy") navigate("/privacy");
          else if (page === "terms-and-conditions") navigate("/terms");
          else if (page === "risk-warning") navigate("/risk-warning");
          else if (page === "contact") navigate("/contact");
          else if (page === "home") onBack();
          else if (page === "changelog") navigate("/changelog");
        })} 
      />
    </div>
  )
}

export default Documentation
